// @ds/connected/CommentThread — data-driven wrapper around the presentational
// @ds/activity/CommentThread.
//
// Owns:
//   - Refine useTable<Comment> (fetch + refetch)
//   - useState for showResolved
//   - sort/filter logic (delegated to the presentational layer)
//
// Renders CommentThread from @ds/activity/ with fetched data + mutation callbacks.
// Production consumers stay configuration-only (commentableType + commentableId).

import { useEffect, useMemo, useState } from "react";
import { useTable, HttpError } from "@refinedev/core";
import type { Comment } from "@shared/types/employee";
import type { CommentableType } from "@shared/types";
import { COMMENTS_FULL_SELECT } from "./comments.constants";
import { CommentCard } from "./CommentCard";
import { CommentComposer } from "./CommentComposer";
import { CommentThread as CommentThreadPresentation } from "@ds/activity/CommentThread";

// ─── Inputs (data props — the model) ──────────────────────────────────────────

export interface CommentThreadInput {
  commentableType: CommentableType;
  commentableId:   number;
  /** `data-testid` forwarded to the presentational root. */
  testId?: string;
}

export interface CommentThreadOutput {
  /** Fires with the latest total so parent chrome can render count badges. */
  onCount?: (count: number | undefined) => void;
}

export type CommentThreadProps = CommentThreadInput & CommentThreadOutput;

// ─── CommentThread (connected) ────────────────────────────────────────────────

export function CommentThread({ commentableType, commentableId, testId, onCount }: CommentThreadProps) {
  const [showResolved, setShowResolved] = useState(false);

  const commentsTable = useTable<Comment, HttpError>({
    resource:         "comments",
    syncWithLocation: false,
    filters: {
      permanent: [
        { field: "commentable_type", operator: "eq", value: commentableType },
        { field: "commentable_id",   operator: "eq", value: commentableId   },
        { field: "is_deleted",       operator: "eq", value: false           },
      ],
    },
    sorters: {
      initial: [
        { field: "is_pinned",  order: "desc" },
        { field: "created_at", order: "desc" },
      ],
    },
    pagination: { pageSize: 100 },
    meta:       { select: COMMENTS_FULL_SELECT },
  });

  const total       = commentsTable.tableQuery.data?.total;
  const refetch     = () => commentsTable.tableQuery.refetch();

  useEffect(() => {
    onCount?.(total);
  }, [onCount, total]);

  // Keep a stable array reference when data hasn't changed.
  // useMemo avoids re-rendering the presentational tree on every render
  // of this wrapper when the comment list hasn't actually changed.
  const comments = useMemo(
    () => commentsTable.tableQuery.data?.data ?? [],
    [commentsTable.tableQuery.data?.data],
  );

  return (
    <CommentThreadPresentation
      comments={comments}
      showResolved={showResolved}
      onShowResolvedChange={setShowResolved}
      testId={testId}
      renderCard={(c: Comment) => (
        <CommentCard
          key={c.id}
          comment={c}
          mode="full"
          onMutated={refetch}
        />
      )}
      renderComposer={() => (
        <CommentComposer
          commentableType={commentableType}
          commentableId={commentableId}
          onCreated={refetch}
        />
      )}
    />
  );
}
